import { useEffect, useRef, useState } from "react";

export type MutationCallback<T extends HTMLElement = HTMLElement> = (
	mutations: MutationRecord[],
	ref: NonNullable<T>,
) => void;

export const useMutationObserverCallback = <T extends HTMLElement = HTMLElement>(
	args: MutationObserverInit,
	callback: MutationCallback<T>,
) => {
	const ref = useRef<T>(null);

	useEffect(() => {
		if (!ref.current) return;

		const observer = new MutationObserver((mutations) => {
			callback(mutations, ref.current as NonNullable<T>);
		});
		observer.observe(ref.current, args);
		return () => {
			observer.disconnect();
		};
	}, [args, callback]);

	return ref;
};

export const useMutationObserver = <T extends HTMLElement = HTMLElement>(
	args: MutationObserverInit,
) => {
	const [state, setRecords] = useState<MutationRecord[] | null>(null);

	const ref = useMutationObserverCallback<T>(args, (mutations) => {
		setRecords(mutations);
	});

	return [ref, state] as const;
};
